/* Fleur cart ETA capture. Ordinary checkout only; see README installation gates. */
(function (scope) {
  'use strict';
  const KEY = '_preorder_eta';
  const NONE = 'In stock';
  const TBC = 'Arrival date to be confirmed.';
  const STATE_URL = 'https://inventory.fleurstudios.com.au/api/storefront/preorder-state';
  const STORES = {
    'https://fleurstudios.com.au': 'sydney',
    'https://www.fleurstudios.com.au': 'sydney',
    'https://fleurstudios.com': 'us',
    'https://www.fleurstudios.com': 'us',
  };
  const MESSAGE =
    'We could not refresh the arrival information. Please try again before continuing.';

  function createController({
    fetch,
    origin,
    root = '/',
    monotonic = () => scope.performance.now(),
    timeoutMs = 15000,
  }) {
    let tail = Promise.resolve();
    const store = STORES[origin];
    const serialized = (task) => {
      const result = tail.then(task);
      tail = result.catch(() => {});
      return result;
    };
    async function json(url, options = {}) {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), timeoutMs);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          signal: abort.signal,
        });
        if (!response.ok) throw new Error('Arrival request failed');
        return { data: await response.json(), date: response.headers?.get('date') };
      } finally {
        clearTimeout(timer);
      }
    }
    function localUrl(path) {
      const url = new URL(path, origin);
      if (url.origin !== origin) throw new Error('Unexpected store');
      return url;
    }
    async function readState({ productId, variantId }) {
      productId = String(productId);
      variantId = String(variantId);
      if (!store || ![productId, variantId].every((id) => /^[1-9][0-9]{0,19}$/.test(id)))
        throw new Error('Missing product identity');
      const nonce = scope.crypto.randomUUID();
      const url = new URL(STATE_URL);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('variant_id', variantId);
      url.searchParams.set('nonce', nonce);
      const started = monotonic();
      const { data, date } = await json(url.href, { credentials: 'omit' });
      const elapsed = monotonic() - started;
      const observedTime = data?.observed_at;
      const responseTime = date ? Date.parse(date) : NaN;
      if (
        data?.schema !== 2 ||
        data.store !== store ||
        data.product_id !== productId ||
        data.variant_id !== variantId ||
        data.nonce !== nonce ||
        typeof data.preorder !== 'boolean' ||
        !Number.isSafeInteger(observedTime) ||
        !Number.isFinite(responseTime) ||
        Math.abs(responseTime - observedTime) > 60000 ||
        !Number.isFinite(elapsed) ||
        elapsed < 0 ||
        elapsed > timeoutMs
      )
        throw new Error('Unverified arrival state');
      if (!data.preorder) {
        if (data.eta !== NONE) throw new Error('Unknown arrival state');
        return NONE;
      }
      const eta = typeof data.eta === 'string' ? data.eta : '';
      const warehouse = store === 'sydney' ? 'Sydney' : 'US';
      if (
        eta === TBC ||
        new RegExp(`^Estimated arrival to our ${warehouse} warehouse between .+ - .+\\.$`).test(eta)
      )
        return eta;
      throw new Error('Unknown preorder arrival');
    }
    const cartUrl = (path) => localUrl(`${root}${path}`).href;
    async function readCart() {
      const { data } = await json(cartUrl('cart.js'));
      if (
        !Array.isArray(data?.items) ||
        data.items.some(
          (i) =>
            !i.key ||
            !i.variant_id ||
            !i.product_id ||
            !Number.isInteger(i.quantity) ||
            i.quantity < 1,
        )
      )
        throw new Error('Invalid cart');
      return data;
    }
    const properties = (item) =>
      item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)
        ? item.properties
        : {};
    const canonical = (object) =>
      JSON.stringify(Object.entries(object).sort(([a], [b]) => a.localeCompare(b)));
    function lineIdentity(item) {
      const foreign = { ...properties(item) };
      delete foreign[KEY];
      return JSON.stringify([
        item.product_id,
        item.variant_id,
        item.selling_plan_allocation?.selling_plan?.id ?? null,
        canonical(foreign),
      ]);
    }
    function invariant(cart) {
      const groups = new Map();
      for (const item of cart.items) {
        const identity = lineIdentity(item);
        groups.set(identity, (groups.get(identity) ?? 0) + item.quantity);
      }
      return canonical(Object.fromEntries(groups));
    }
    async function reconcileCart({
      assertUnchanged = () => {},
      onLineKeysChanged = () => {},
    } = {}) {
      let cart = await readCart();
      const before = invariant(cart);
      const states = new Map();
      const identity = (item) => `${item.product_id}:${item.variant_id}`;
      const cartVersion = (value) =>
        JSON.stringify(
          value.items.map((item) => [
            item.key,
            item.quantity,
            canonical(properties(item)),
            item.selling_plan_allocation?.selling_plan?.id ?? null,
          ]),
        );
      // Re-read every response: changing properties changes line keys and may merge identical lines.
      const limit = cart.items.length * 2 + 3;
      for (let step = 0; step < limit; step++) {
        let changed = false;
        for (const item of cart.items) {
          if (!states.has(identity(item)))
            states.set(
              identity(item),
              await readState({
                url: item.url,
                productId: item.product_id,
                variantId: item.variant_id,
              }),
            );
          assertUnchanged();
          const eta = states.get(identity(item));
          if (properties(item)[KEY] === eta) continue;
          const latest = await readCart();
          if (cartVersion(latest) !== cartVersion(cart))
            throw new Error('Cart changed while refreshing; retry');
          assertUnchanged();
          const result = await json(cartUrl('cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: { ...properties(item), [KEY]: eta },
            }),
          });
          // Publish the response's keys before the next network read can fail. A queued
          // quantity edit must use the returned key in both its request and theme events.
          const updated = result.data;
          if (!Array.isArray(updated?.items) || invariant(updated) !== before)
            throw new Error('Cart changed while refreshing; retry');
          const changes = new Map();
          for (const previous of cart.items) {
            if (updated.items.some((next) => next.key === previous.key)) continue;
            const matches = updated.items.filter(
              (next) => lineIdentity(next) === lineIdentity(previous),
            );
            changes.set(
              previous.key,
              matches.length === 1 && matches[0].quantity === previous.quantity
                ? matches[0].key
                : null,
            );
          }
          onLineKeysChanged(changes);
          cart = await readCart();
          if (invariant(cart) !== before) throw new Error('Cart changed while refreshing; retry');
          assertUnchanged();
          changed = true;
          break;
        }
        if (!changed) {
          const confirmed = await readCart();
          if (cartVersion(confirmed) !== cartVersion(cart))
            throw new Error('Cart changed while refreshing; retry');
          assertUnchanged();
          return confirmed;
        }
      }
      throw new Error('Arrival information changed while refreshing; retry');
    }
    async function prepareForm(form) {
      const source = form.querySelector('[data-fleur-preorder-source]');
      if (!source) throw new Error('Missing product arrival source');
      for (let attempt = 0; attempt < 3; attempt++) {
        const variantId = form.elements.namedItem('id')?.value;
        if (!variantId) throw new Error('Select a product variant');
        const eta = await readState({
          url: source.dataset.productUrl,
          productId: source.dataset.productId,
          variantId,
        });
        if (String(form.elements.namedItem('id')?.value) !== String(variantId)) continue;
        const fields = [...form.querySelectorAll(`[name="properties[${KEY}]"]`)];
        const field = fields.shift() || form.ownerDocument.createElement('input');
        field.type = 'hidden';
        field.name = `properties[${KEY}]`;
        field.value = eta;
        field.disabled = false;
        if (!field.parentNode) form.append(field);
        fields.forEach((input) => input.remove());
        return eta;
      }
      throw new Error('Product variant changed; retry');
    }
    return { readState, readCart, reconcileCart, prepareForm, serialized };
  }

  function install(win) {
    if (win.__fleurPreorderCartInstalled) return win.__fleurPreorderCartInstalled;
    const doc = win.document;
    const origin = win.location.origin;
    const originalFetch = win.fetch.bind(win);
    const root = win.Shopify?.routes?.root || '/';
    const controller = createController({ fetch: originalFetch, origin, root });
    const pending = new WeakSet();
    const allowed = new WeakSet();
    const errors = new WeakMap();
    const keyChanges = new Map();
    let cartLinesRefreshed = false;
    let cartMutationVersion = 0;
    let quantityFetch = false;
    let addFetches = null;
    function path(value) {
      try {
        const url = new URL(value, origin);
        return url.origin === origin ? url.pathname : '';
      } catch {
        return '';
      }
    }
    function currentKey(key) {
      const visited = new Set();
      while (keyChanges.has(key)) {
        if (visited.has(key) || keyChanges.get(key) === null)
          throw new Error('Cart lines combined; reload cart');
        visited.add(key);
        key = keyChanges.get(key);
      }
      return key;
    }
    function currentCartRequest(url, options) {
      if (typeof options?.body !== 'string') return options;
      const type = new Headers(options.headers).get('content-type')?.split(';')[0].trim().toLowerCase();
      if (/\/cart\/change(?:\.js)?$/.test(path(url)) && type === 'application/x-www-form-urlencoded') {
        const fields = new URLSearchParams(options.body);
        const ids = fields.getAll('id');
        const lines = fields.getAll('line');
        if (ids.length > 1 || lines.length > 1 || (ids.length && lines.length))
          throw new Error('Ambiguous cart target; reload cart');
        // Rebuy's positional target may come from its pre-refresh cart snapshot.
        // Only key aliases are authoritative after a property write/reorder/merge.
        if (lines.length && cartLinesRefreshed) throw new Error('Cart lines changed; reload cart');
        if (!ids.length) return options;
        const key = currentKey(ids[0]);
        if (key === ids[0]) return options;
        // Replace only the id value. Keep other fields, repeats and encoding byte-for-byte.
        const body = options.body.split('&').map((field) => new URLSearchParams(field).has('id')
          ? `${field.slice(0, field.indexOf('='))}=${encodeURIComponent(key)}` : field).join('&');
        return { ...options, body };
      }
      if (/\/cart\/update(?:\.js)?$/.test(path(url)) && type === 'application/json') {
        let data;
        try { data = JSON.parse(options.body); } catch { return options; }
        if (!data?.updates || typeof data.updates !== 'object') return options;
        if (Array.isArray(data.updates)) {
          if (cartLinesRefreshed) throw new Error('Cart lines changed; reload cart');
          return options;
        }
        const updates = new Map();
        let changed = false;
        for (const [key, quantity] of Object.entries(data.updates)) {
          const next = currentKey(key);
          if (updates.has(next)) throw new Error('Ambiguous cart target; reload cart');
          updates.set(next, quantity);
          changed ||= next !== key;
        }
        return changed ? { ...options, body: JSON.stringify({ ...data, updates: Object.fromEntries(updates) }) } : options;
      }
      return options;
    }
    // Existing theme add/change/update requests share the checkout lock. Internal reconciliation
    // uses originalFetch, so it cannot deadlock behind its own lock.
    win.fetch = function (input, options) {
      const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
      if (/\/cart\/(?:add|change|update|clear)(?:\.js)?$/.test(path(url))) {
        // The captured theme quantity method starts its fetch synchronously. Its outer
        // wrapper already owns the queue until the response and section events finish.
        if (quantityFetch) return originalFetch(input, options);
        if (addFetches) {
          const response = originalFetch(input, options);
          addFetches.push(response);
          return response;
        }
        cartMutationVersion++;
        return controller.serialized(() => originalFetch(input, currentCartRequest(url, options)));
      }
      return originalFetch(input, options);
    };
    async function prepareCheckout() {
      const version = cartMutationVersion;
      await controller.reconcileCart({
        assertUnchanged() {
          if (version !== cartMutationVersion)
            throw new Error('Cart changed while refreshing; retry');
        },
        onLineKeysChanged(changes) {
          cartLinesRefreshed = true;
          for (const [from, to] of changes) {
            // Shopify keys are deterministic and can return to a formerly used value.
            if (to) keyChanges.delete(to);
            keyChanges.set(from, to);
          }
          // The ordinary page-cart handler navigates using these rendered keys.
          // Update only exact owned rekeys; quantity values and presentation stay intact.
          for (const input of doc.querySelectorAll('[data-line-key]')) {
            const next = changes.get(input.getAttribute('data-line-key'));
            if (next) input.setAttribute('data-line-key', next);
          }
          for (const anchor of doc.querySelectorAll('a[href]')) {
            if (!/\/cart\/change(?:\.js)?$/.test(path(anchor.href))) continue;
            const url = new URL(anchor.href, origin);
            const next = changes.get(url.searchParams.get('id'));
            if (next) {
              url.searchParams.set('id', next);
              anchor.href = url.href;
            }
          }
        },
      });
    }
    function showError(target) {
      let alert = errors.get(target);
      if (!alert) {
        alert = doc.createElement('p');
        alert.setAttribute('data-fleur-preorder-error', '');
        alert.setAttribute('role', 'alert');
        (target.tagName === 'FORM' ? target : target.parentElement || doc.body).append(alert);
        errors.set(target, alert);
      }
      alert.textContent = MESSAGE + ' If the cart changed, reload it before trying again.';
      target.dispatchEvent(
        new win.CustomEvent('cart:error', { bubbles: true, detail: { error: MESSAGE } }),
      );
    }
    // Wrap the existing quantity method, not its response. It must receive the new key
    // before it fetches AND filters the response for its line-item:change event.
    win.customElements.whenDefined('line-item-quantity').then(() => {
      const prototype = win.customElements.get('line-item-quantity').prototype;
      const original = prototype._changeLineItemQuantity;
      if (typeof original !== 'function') return;
      prototype._changeLineItemQuantity = function (key, quantity) {
        cartMutationVersion++;
        return controller
          .serialized(async () => {
            key = currentKey(key);
            let result;
            quantityFetch = true;
            try {
              result = original.call(this, key, quantity);
            } finally {
              quantityFetch = false;
            }
            return await result;
          })
          .catch(() => showError(this));
      };
    });
    async function guard(target, task, resume) {
      if (pending.has(target)) return;
      pending.add(target);
      const previousBusy = target.getAttribute('aria-busy');
      target.setAttribute('aria-busy', 'true');
      errors.get(target)?.remove();
      errors.delete(target);
      try {
        await controller.serialized(async () => {
          await task();
          await resume();
        });
      } catch {
        showError(target);
      } finally {
        pending.delete(target);
        if (previousBusy === null) target.removeAttribute('aria-busy');
        else target.setAttribute('aria-busy', previousBusy);
      }
    }
    function stop(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    doc.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof win.HTMLFormElement)) return;
        if (allowed.has(form)) {
          allowed.delete(form);
          return;
        }
        const action = path(
          event.submitter?.hasAttribute('formaction') ? event.submitter.formAction : form.action,
        );
        const add = /\/cart\/add(?:\.js)?$/.test(action);
        const checkout =
          /\/checkout\/?$/.test(action) ||
          (/\/cart\/?$/.test(action) && event.submitter?.name === 'checkout');
        if (!add && !checkout) return;
        stop(event);
        if (add) cartMutationVersion++;
        const submitter = event.submitter;
        void guard(
          form,
          () => (add ? controller.prepareForm(form) : prepareCheckout()),
          () => {
            allowed.add(form);
            if (add) addFetches = [];
            let requests;
            try {
              form.requestSubmit(submitter || undefined);
            } finally {
              allowed.delete(form);
              if (add) {
                requests = addFetches;
                addFetches = null;
              }
            }
            return requests ? Promise.all(requests) : undefined;
          },
        );
      },
      true,
    );
    doc.addEventListener(
      'click',
      (event) => {
        const anchor = event.target?.closest?.('a[href]');
        // Rebuy's active Smart Cart uses a plain button with its own click handler.
        // Replay that same button after reconciliation, preserving its app behavior.
        const target = anchor && /\/checkout\/?$/.test(path(anchor.href))
          ? anchor
          : event.target?.closest?.('button.rebuy-cart__checkout-button');
        if (!target) return;
        if (allowed.has(target)) {
          allowed.delete(target);
          return;
        }
        stop(event);
        void guard(target, prepareCheckout, () => {
          allowed.add(target);
          try {
            target.click();
          } finally {
            allowed.delete(target);
          }
        });
      },
      true,
    );
    win.__fleurPreorderCartInstalled = controller;
    return controller;
  }
  const api = { createController, install, KEY, NONE, TBC };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else {
    scope.FleurPreorderCart = api;
    install(scope);
  }
})(typeof window !== 'undefined' ? window : globalThis);
